import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import "../../../css/allFacility.css";
import axios from "axios";
import { Default } from "../../layouts/default";
import { Link } from "react-router-dom";
import LazyImage from "../../common/LazyImage";

import iconCricket from "../../../image/1.png";
import iconShuttle from "../../../image/2.png";
import iconBasket from "../../../image/3.png";
import iconTennis from "../../../image/4.png";
import iconBats from "../../../image/5.png";
import iconPool from "../../../image/6.png";
import iconVolleyball from "../../../image/7.png";
import iconFootball from "../../../image/8.png";
import bannerBv from "../../../image/banner-bv.png";

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const IMG_URL = process.env.REACT_APP_IMG_URL;

const FALLBACK_VENUE_IMG =
  "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&q=85&auto=format&fit=crop";

/* Same sport imagery as homepage bv-hero__chips */
const FILTER_CARDS = [
  { id: "all", label: "All venues", img: iconPool, keywords: [] },
  {
    id: "cricket",
    label: "Cricket grounds",
    img: iconCricket,
    keywords: ["cricket", "ground", "bat"],
  },
  {
    id: "football",
    label: "Football turfs",
    img: iconFootball,
    keywords: ["football", "soccer", "turf", "futsal"],
  },
  {
    id: "badminton",
    label: "Badminton courts",
    img: iconShuttle,
    keywords: ["badminton", "shuttle", "racquet", "racket"],
  },
  {
    id: "pickleball",
    label: "Pickleball courts",
    img: iconTennis,
    keywords: ["pickleball", "pickle"],
  },
  {
    id: "court",
    label: "Indoor courts",
    img: iconVolleyball,
    keywords: ["court", "indoor", "volleyball", "basketball"],
  },
  {
    id: "fitness",
    label: "Fitness & gym",
    img: iconBasket,
    keywords: ["fitness", "gym", "community", "sports"],
  },
  {
    id: "more",
    label: "More sports",
    img: iconBats,
    keywords: ["swim", "pool", "multi", "sport"],
  },
];

const sportImgByName = (raw) => {
  const n = (raw || "").toLowerCase();
  if (/foot|soccer|turf/.test(n)) return iconFootball;
  if (/cricket|bat/.test(n)) return iconCricket;
  if (/badm|shuttle|racquet|racket|pickle/.test(n)) return iconShuttle;
  if (/basket/.test(n)) return iconBasket;
  if (/tennis/.test(n)) return iconTennis;
  if (/volley/.test(n)) return iconVolleyball;
  if (/pool|swim|table/.test(n)) return iconPool;
  if (/baseball|gym|fitness/.test(n)) return iconBats;
  return iconFootball;
};

const facilityMatchesFilter = (item, filterId) => {
  if (filterId === "all") return true;
  const card = FILTER_CARDS.find((c) => c.id === filterId);
  if (!card?.keywords?.length) return true;

  const haystack = [
    item?.official_name,
    item?.description,
    item?.address,
    ...(item?.services || []).map((s) => s?.name),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return card.keywords.some((kw) => haystack.includes(kw));
};

const AllFacility = () => {
  const cleanDescription = (desc) => {
    if (typeof desc !== "string") return "";
    return desc.replace(/^"|"$/g, "").replace(/\\"/g, '"');
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const normalizeImg = (path) => {
    if (!path) return "";
    return `${IMG_URL}${path.replace(/\\\//g, "/")}`;
  };

  const [allFeatured, setAllFeatured] = useState({ facility: [] });
  const [sortType, setSortType] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeFilterCard =
    FILTER_CARDS.find((c) => c.id === activeFilter) || FILTER_CARDS[0];

  useEffect(() => {
    const fetchFeaturedData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BASE_URL}/get-all-facility`);
        setAllFeatured(res.data);
      } catch (err) {
        console.error("Error fetching facilities:", err);
        setError("Failed to load facilities. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(
      ".af-page .bv-reveal:not(.is-revealed)",
    );
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [loading, error, activeFilter, sortType, searchQuery, allFeatured]);

  const displayedFacilities = useMemo(() => {
    let list = [...(allFeatured.facility || [])].filter((item) =>
      facilityMatchesFilter(item, activeFilter),
    );

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((item) => {
        const haystack = [
          item?.official_name,
          item?.address,
          item?.description,
          ...(item?.services || []).map((s) => s?.name),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (sortType === "price") {
      list.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortType === "name") {
      list.sort((a, b) =>
        (a.official_name || "").localeCompare(b.official_name || ""),
      );
    }
    return list;
  }, [allFeatured.facility, activeFilter, sortType, searchQuery]);

  const totalCount = allFeatured.facility?.length || 0;

  const filterCounts = useMemo(() => {
    const facilities = allFeatured.facility || [];
    const counts = { all: facilities.length };
    FILTER_CARDS.forEach((card) => {
      if (card.id === "all") return;
      counts[card.id] = facilities.filter((item) =>
        facilityMatchesFilter(item, card.id),
      ).length;
    });
    return counts;
  }, [allFeatured.facility]);

  const toolbarStatus = useMemo(() => {
    if (loading) {
      return { type: "loading" };
    }

    const count = displayedFacilities.length;
    const q = searchQuery.trim();

    if (count === 0) {
      return {
        type: "empty",
        message: q
          ? "No venues match your search"
          : "No venues for this sport — try another type",
      };
    }

    if (q) {
      return {
        type: "line",
        count,
        text: `matching “${q}”${totalCount > count ? ` (${totalCount} on site)` : ""}`,
      };
    }

    if (activeFilter !== "all") {
      return {
        type: "line",
        count,
        text: `in ${activeFilterCard.label}${totalCount > count ? ` · ${totalCount} on site` : ""}`,
      };
    }

    return {
      type: "line",
      count,
      text: "available to book",
    };
  }, [
    loading,
    displayedFacilities.length,
    searchQuery,
    activeFilter,
    activeFilterCard,
    totalCount,
  ]);

  return (
    <Default>
      <Helmet>
        <title>
          Book Sports Venues Online | Cricket Grounds, Football Turfs & Courts
        </title>
        <meta
          name="description"
          content="Book cricket grounds, football turfs, badminton courts, pickleball arenas, gyms, and sports facilities online with Book Venue. Find sports venues near you with easy online booking."
        />
        <meta name="robots" content="index, follow" />
        <Link rel="canonical" to="https://bookvenue.app/facilities" />
      </Helmet>

      <div className="af-page">
        <header className="af-hero">
          <div className="bv-container bv-container--wide af-hero__inner">
            <nav className="af-breadcrumb bv-reveal" aria-label="Breadcrumb">
              <Link to="/" className="af-breadcrumb__link">
                <i className="fa-solid fa-house" aria-hidden="true" />
                Home
              </Link>
              <span className="af-breadcrumb__sep" aria-hidden="true">
                <i className="fa-solid fa-chevron-right" />
              </span>
              <span className="af-breadcrumb__current" aria-current="page">
                All Facilities
              </span>
            </nav>

            <div className="af-hero__body">
              <div className="af-hero__content bv-reveal" data-delay="100">
                <span className="af-hero__eyebrow">
                  <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
                  Explore venues
                </span>
                <h1 className="af-hero__title">
                  Book sports venues{" "}
                  <span className="gradientText">across India</span>
                </h1>
                <p className="af-hero__lede">
                  Discover cricket grounds, football turfs, badminton courts, and
                  more — with real-time availability and instant online booking.
                </p>
              </div>

              <div className="af-hero__visual bv-reveal" data-delay="180">
                <LazyImage
                  className="af-hero__img"
                  src={bannerBv}
                  alt="Players at a sports facility available to book on Book Venue"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </header>

        {/* Sidebar + listings */}
        <section className="af-listings bv-section--cream">
          <div className="bv-container bv-container--wide">
            <div
              className={`af-layout ${filterOpen ? "is-filter-open" : "is-filter-collapsed"}`}
            >
            <aside
              id="af-sport-filters"
              className="af-sidebar bv-reveal bv-reveal--left"
              aria-hidden={!filterOpen}
            >
              <div className="af-sidebar__panel">
                <div className="af-sidebar__head">
                  <div className="af-sidebar__head-top">
                    <span className="af-sidebar__eyebrow">
                      <i className="fa-solid fa-sliders" aria-hidden="true" />
                      Filters
                    </span>
                    <button
                      type="button"
                      className="af-sidebar__collapse"
                      onClick={() => setFilterOpen(false)}
                      aria-label="Close sport type filters"
                    >
                      <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                    </button>
                  </div>
                  <h2 className="af-sidebar__title">Sport type</h2>
                  <p className="af-sidebar__hint">
                    Choose a sport to see matching venues
                  </p>
                </div>

                <nav
                  className="af-sidebar__filters"
                  role="tablist"
                  aria-label="Filter facilities by sport"
                >
                  {FILTER_CARDS.map((card) => {
                    const isActive = activeFilter === card.id;
                    const count = loading ? null : filterCounts[card.id] ?? 0;
                    return (
                      <button
                        key={card.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`af-sidebar__filter ${isActive ? "is-active" : ""}`}
                        onClick={() => setActiveFilter(card.id)}
                      >
                        <span className="af-sidebar__filter-icon bv-hero__chip-icon">
                          <LazyImage src={card.img} alt="" />
                        </span>
                        <span className="af-sidebar__filter-text">
                          <span className="af-sidebar__filter-label">
                            {card.label}
                          </span>
                          {!loading && (
                            <span className="af-sidebar__filter-count">
                              {count} venue{count === 1 ? "" : "s"}
                            </span>
                          )}
                        </span>
                        {isActive && (
                          <i
                            className="fa-solid fa-check af-sidebar__filter-check"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </nav>

                <div className="af-sidebar__divider" aria-hidden="true" />

                <div className="af-sidebar__sort-block">
                  <label htmlFor="af-sortby" className="af-sidebar__sort-label">
                    <i className="fa-solid fa-arrow-down-wide-short" aria-hidden="true" />
                    Sort results
                  </label>
                  <div className="af-select-wrap">
                    <select
                      id="af-sortby"
                      className="af-select af-select--sidebar"
                      value={sortType}
                      onChange={(e) => setSortType(e.target.value)}
                    >
                      <option value="">Recommended</option>
                      <option value="price">Price: low to high</option>
                      <option value="name">Name: A–Z</option>
                    </select>
                    <i
                      className="fa-solid fa-chevron-down af-select__chevron"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
            </aside>

            <div className="af-main">
              <div className="af-toolbar bv-reveal">
                <div className="af-toolbar__left">
                  <button
                    type="button"
                    className={`af-toolbar__filter-toggle ${filterOpen ? "is-open" : ""}`}
                    onClick={() => setFilterOpen((v) => !v)}
                    aria-expanded={filterOpen}
                    aria-controls="af-sport-filters"
                  >
                    <span className="af-toolbar__filter-toggle-icon" aria-hidden="true">
                      <i className="fa-solid fa-filter" />
                    </span>
                    <span className="af-toolbar__filter-toggle-text">
                      {filterOpen ? "Close filters" : "Sport filters"}
                    </span>
                    <i
                      className={`fa-solid fa-chevron-${filterOpen ? "left" : "right"} af-toolbar__filter-toggle-chev`}
                      aria-hidden="true"
                    />
                  </button>

                  <div className="af-toolbar__status" aria-live="polite">
                    {toolbarStatus.type === "loading" && (
                      <span className="af-toolbar__status-line af-toolbar__status-line--loading">
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                        Finding venues…
                      </span>
                    )}
                    {toolbarStatus.type === "empty" && (
                      <span className="af-toolbar__status-line af-toolbar__status-line--muted">
                        {toolbarStatus.message}
                      </span>
                    )}
                    {toolbarStatus.type === "line" && (
                      <span className="af-toolbar__status-line">
                        <strong>{toolbarStatus.count}</strong>
                        {toolbarStatus.count === 1 ? " venue " : " venues "}
                        {toolbarStatus.text}
                      </span>
                    )}
                  </div>
                </div>

                <div className="af-toolbar__right">
                  {!filterOpen && (
                    <div className="af-toolbar__sort">
                      <label htmlFor="af-sortby-bar" className="af-toolbar__sort-label">
                        <i className="fa-solid fa-arrow-down-wide-short" aria-hidden="true" />
                        <span>Sort</span>
                      </label>
                      <div className="af-select-wrap af-select-wrap--toolbar">
                        <select
                          id="af-sortby-bar"
                          className="af-select af-select--toolbar"
                          value={sortType}
                          onChange={(e) => setSortType(e.target.value)}
                        >
                          <option value="">Recommended</option>
                          <option value="price">Price: low to high</option>
                          <option value="name">Name: A–Z</option>
                        </select>
                        <i
                          className="fa-solid fa-chevron-down af-select__chevron"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  )}

                  <div className="af-toolbar__search">
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                    <input
                      type="search"
                      className="af-toolbar__search-input"
                      placeholder="Search venues "
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Search venues"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="af-toolbar__search-clear"
                        onClick={() => setSearchQuery("")}
                        aria-label="Clear search"
                      >
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {error && (
              <div className="af-alert bv-reveal" role="alert">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                {error}
              </div>
            )}

            {loading && (
              <div className="af-grid af-grid--skeleton" aria-busy="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div className="af-venue-card af-venue-card--skeleton" key={`sk-${i}`}>
                    <div className="af-skeleton af-skeleton--img" />
                    <div className="af-skeleton af-skeleton--line af-skeleton--title" />
                    <div className="af-skeleton af-skeleton--line af-skeleton--short" />
                    <div className="af-skeleton af-skeleton--line" />
                  </div>
                ))}
              </div>
            )}

            {!loading && !error && displayedFacilities.length > 0 && (
              <div className="af-grid">
                {displayedFacilities.map((item, index) => {
                  const imgSrc =
                    normalizeImg(item?.featured_image) || FALLBACK_VENUE_IMG;
                  const serviceTags = (item?.services || []).slice(0, 3);
                  const priceLabel =
                    item?.price || item?.minPrice
                      ? `₹${item.price || item.minPrice}/hr`
                      : null;
                  const delay = Math.min((index % 6) * 80, 400);

                  return (
                    <article
                      key={`${item?.slug}-${index}`}
                      className="af-venue-card bv-reveal"
                      data-delay={String(delay)}
                    >
                      <Link
                        to={`/facility/${item?.slug}`}
                        className="af-venue-card__link"
                      >
                        <div className="af-venue-card__media">
                          <LazyImage
                            src={imgSrc}
                            alt={item?.official_name || "Venue"}
                            onError={(e) => {
                              e.currentTarget.src = FALLBACK_VENUE_IMG;
                            }}
                          />
                          <div className="af-venue-card__media-shine" aria-hidden="true" />
                          <div className="af-venue-card__media-overlay" aria-hidden="true" />

                          <div className="af-venue-card__media-top">
                            {priceLabel && (
                              <span className="af-venue-card__price">{priceLabel}</span>
                            )}
                            <span className="af-venue-card__avail">
                              <i className="fa-solid fa-bolt" aria-hidden="true" />
                              Instant book
                            </span>
                          </div>

                        </div>

                        <div className="af-venue-card__body">
                          <h3 className="af-venue-card__title">
                            {item?.official_name || "Unnamed Facility"}
                          </h3>

                          <p className="af-venue-card__loc">
                            <span className="af-venue-card__loc-icon">
                              <i className="fa-solid fa-location-dot" aria-hidden="true" />
                            </span>
                            {truncateText(item?.address, 56) || "Location on request"}
                          </p>

                          <p className="af-venue-card__desc">
                            {item?.description
                              ? truncateText(
                                  cleanDescription(item.description),
                                  72,
                                )
                              : "Premium sports facility with flexible booking slots."}
                          </p>

                          {serviceTags.length > 0 && (
                            <div className="af-venue-card__tags">
                              {serviceTags.map((service, idx) => (
                                <span
                                  key={`${service?.name || "svc"}-${idx}`}
                                  className="af-venue-card__tag bv-hero__chip"
                                >
                                  <span className="bv-hero__chip-icon">
                                    <LazyImage
                                      src={sportImgByName(service?.name)}
                                      alt=""
                                    />
                                  </span>
                                  {service?.name || "Sport"}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="af-venue-card__footer">
                            <span className="af-venue-card__cta">
                              Book now
                              <span className="af-venue-card__cta-icon">
                                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                              </span>
                            </span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  );
                })}
              </div>
            )}

            {!loading && !error && displayedFacilities.length === 0 && (
              <div className="af-empty bv-reveal">
                <div className="af-empty__icon" aria-hidden="true">
                  <i className="fa-solid fa-magnifying-glass-location" />
                </div>
                <h3>No venues in this category</h3>
                <p>
                  Try another sport filter or browse all venues to see everything
                  available.
                </p>
                <button
                  type="button"
                  className="bv-btn bv-btn--primary"
                  onClick={() => setActiveFilter("all")}
                >
                  View all venues
                </button>
              </div>
            )}
            </div>
            </div>
          </div>
        </section>
      </div>
    </Default>
  );
};

export default AllFacility;
